import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function BishopscourtPage() {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    try {
      document.title = 'Bishopscourt Painters Cape Town | Luxury Estate Painting Services | Premium Property Painting';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Premium painting services for Bishopscourt luxury estates. Specialist high-end residential painting for exclusive properties with expert craftsmanship and attention to detail.'
        );
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'Bishopscourt painters, luxury estate painting, premium property painting'
        );
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bishopscourt`
        );
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Bishopscourt Painters Cape Town',
        description: 'Premium painting services for Bishopscourt luxury estates',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bishopscourt`,
        areaServed: {
          '@type': 'Place',
          name: 'Bishopscourt, Cape Town, Western Cape, South Africa',
        },
        serviceArea: {
          '@type': 'GeoCircle',
          geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: -33.9897,
            longitude: 18.4532,
          },
          geoRadius: '10000',
        },
      });
      document.head.appendChild(script);
    } catch (err) {
      console.error('Error setting up meta tags or JSON-LD script:', err);
    }

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Bishopscourt Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Luxury Estate Painting',
      description: 'Complete interior and exterior painting for Bishopscourt luxury estates with premium materials and meticulous attention to detail. Our team specializes in high-end residential properties, understanding the unique requirements of luxury homes including extensive surface areas, intricate architectural details, and the need for premium finishes that reflect the property\'s prestige.',
      price: 'From R35,000',
      features: ['Premium finishes', 'Heritage restoration', 'Color consultation', 'Minimal disruption', 'Quality guarantee', 'Designer coordination']
    },
    {
      icon: 'ri-building-line',
      title: 'Historic Property Restoration',
      description: 'Specialized restoration painting for Bishopscourt\'s historic properties, preserving architectural heritage with modern protection. We work with conservation specialists to ensure period-appropriate techniques and materials while providing contemporary weather protection and durability for these irreplaceable properties.',
      price: 'From R30,000',
      features: ['Heritage expertise', 'Period-appropriate colors', 'Restoration techniques', 'Architectural preservation', 'Expert craftsmanship', 'Conservation standards']
    },
    {
      icon: 'ri-palette-line',
      title: 'Premium Interior Design',
      description: 'Luxury interior painting with designer finishes, feature walls, and custom color schemes for discerning Bishopscourt clients. We collaborate with interior designers and architects to achieve sophisticated finishes including Venetian plaster, metallic effects, and bespoke color matching.',
      price: 'From R25,000',
      features: ['Designer finishes', 'Custom colors', 'Feature walls', 'Specialty techniques', 'Luxury materials', 'Texture applications']
    },
    {
      icon: 'ri-shield-line',
      title: 'Exterior Estate Protection',
      description: 'Comprehensive exterior painting services designed to protect Bishopscourt estates from Cape Town\'s diverse weather conditions. Using premium weather-resistant coatings and advanced application techniques to ensure long-lasting beauty and protection for your property\'s exterior surfaces.',
      price: 'From R40,000',
      features: ['Weather protection', 'UV resistance', 'Moisture barriers', 'Premium coatings', 'Surface preparation', 'Long-term durability']
    },
    {
      icon: 'ri-vip-crown-line',
      title: 'Concierge Painting Service',
      description: 'White-glove painting service for ultra-luxury Bishopscourt estates requiring the highest level of discretion, coordination, and quality. Includes dedicated project management, flexible scheduling around your lifestyle, and seamless coordination with household staff and security.',
      price: 'Custom Quote',
      features: ['Dedicated manager', 'Flexible scheduling', 'Complete discretion', 'Staff coordination', 'Premium materials', 'Perfection guarantee']
    },
    {
      icon: 'ri-ancient-gate-line',
      title: 'Architectural Feature Painting',
      description: 'Specialized painting for architectural features including ornate moldings, decorative plasterwork, columns, and period details. Expert application techniques preserve intricate details while enhancing the visual impact of these distinctive architectural elements.',
      price: 'From R15,000',
      features: ['Detail preservation', 'Specialty techniques', 'Gold leafing', 'Decorative finishes', 'Period accuracy', 'Artistic expertise']
    }
  ];

  const features = [
    {
      title: 'Luxury Estate Expertise',
      description: 'Extensive experience painting Bishopscourt\'s most prestigious properties with understanding of luxury standards and expectations. Our team has completed numerous high-end projects in this exclusive neighborhood, developing specialized knowledge of the architectural styles, materials, and quality standards expected by discerning property owners.'
    },
    {
      title: 'Discretion & Professionalism',
      description: 'Highest levels of professionalism and discretion, respecting the privacy and exclusivity of Bishopscourt estates. All team members undergo background checks and confidentiality training. We coordinate seamlessly with estate managers, security personnel, and household staff to ensure minimal disruption to your daily life.'
    },
    {
      title: 'Premium Materials Only',
      description: 'Exclusive use of premium paints and materials from leading international brands, ensuring exceptional quality and longevity. We source the finest products from manufacturers like Farrow & Ball, Little Greene, and premium European brands, selected specifically for their superior coverage, durability, and aesthetic qualities.'
    },
    {
      title: 'Heritage Preservation',
      description: 'Specialized knowledge in preserving and restoring historic properties while providing modern protection and finishes. Our craftsmen are trained in traditional techniques and work closely with heritage consultants to ensure all work respects the historical significance and architectural integrity of period properties.'
    },
    {
      title: 'Project Management Excellence',
      description: 'Dedicated project managers oversee every aspect of your painting project from initial consultation through final inspection. Detailed scheduling, regular progress updates, and proactive communication ensure your project proceeds smoothly and meets your exact specifications and timeline requirements.'
    },
    {
      title: 'Quality Assurance Program',
      description: 'Comprehensive quality control at every stage including surface preparation verification, application technique monitoring, and multi-point final inspections. We stand behind our work with extensive warranties and are committed to achieving absolute perfection on every luxury estate project.'
    }
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Estate Consultation',
      description: 'Comprehensive assessment of your Bishopscourt estate with detailed planning and premium material selection. We evaluate architectural features, existing conditions, and your aesthetic preferences.'
    },
    {
      step: 2,
      title: 'Custom Planning',
      description: 'Development of detailed project plan including color schemes, material specifications, scheduling, and coordination with other trades or household activities.'
    },
    {
      step: 3,
      title: 'Meticulous Preparation',
      description: 'Thorough surface preparation with protection of valuable furnishings and architectural features. All surfaces are properly prepared to ensure optimal paint adhesion and finish quality.'
    },
    {
      step: 4,
      title: 'Expert Application',
      description: 'Professional painting using premium materials and advanced techniques by experienced craftsmen. Multiple coats applied with precision to achieve flawless results.'
    },
    {
      step: 5,
      title: 'Detail Refinement',
      description: 'Careful attention to architectural details, trim work, and specialty finishes. Our craftsmen ensure every element is perfectly executed to luxury standards.'
    },
    {
      step: 6,
      title: 'Quality Assurance',
      description: 'Detailed inspection and final touches ensuring flawless results meeting luxury estate standards. Complete walkthrough with you to ensure total satisfaction.'
    }
  ];

  const testimonials = [
    {
      name: 'Richard Pemberton',
      property: 'Bishopscourt Estate',
      rating: 5,
      text: 'Exceptional service for our historic estate. The team\'s attention to detail and respect for our property\'s heritage was outstanding. The results are simply magnificent.'
    },
    {
      name: 'Victoria Sinclair',
      property: 'Luxury Residence',
      rating: 5,
      text: 'Professional, discreet, and impeccable workmanship. They transformed our home with premium finishes that exceeded our expectations. Highly recommended for luxury properties.'
    },
    {
      name: 'Andrew van Riebeeck',
      property: 'Historic Manor',
      rating: 5,
      text: 'Outstanding restoration work on our period property. Their expertise in heritage painting and use of premium materials has beautifully preserved our home\'s character.'
    }
  ];

  const projects = [
    {
      title: 'Historic Manor Restoration',
      description: 'Complete restoration of a 1920s Bishopscourt manor, preserving original features while updating with modern protective coatings. This comprehensive project included exterior facade restoration, interior period-appropriate color schemes, and specialized treatment of ornate plasterwork and moldings.',
      image: 'https://public.readdy.ai/ai/img_res/8b938d80c44b30a33380c191ef6d6c10.jpg'
    },
    {
      title: 'Contemporary Estate',
      description: 'Premium painting for a modern Bishopscourt estate featuring designer finishes and custom color schemes throughout. The project showcased contemporary luxury with sophisticated neutral palettes, feature walls with specialty finishes, and seamless integration with high-end interior design.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20luxury%20estate%20in%20Bishopscourt%20with%20contemporary%20architecture%2C%20freshly%20painted%20exterior%2C%20premium%20residential%20property%2C%20professional%20painting%20finish&width=400&height=300&seq=bishopscourt-project-2&orientation=landscape'
    },
    {
      title: 'Garden Estate Renovation',
      description: 'Comprehensive exterior and interior painting for a sprawling Bishopscourt garden estate with multiple buildings. This extensive project required careful coordination across main residence, guest cottages, and outbuildings while maintaining the estate\'s cohesive aesthetic.',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20garden%20estate%20in%20Bishopscourt%20with%20lush%20landscaping%2C%20multiple%20buildings%20freshly%20painted%2C%20prestigious%20property%2C%20professional%20estate%20painting&width=400&height=300&seq=bishopscourt-project-3&orientation=landscape'
    },
    {
      title: 'Heritage Villa Transformation',
      description: 'Sensitive restoration and modernization of a Cape Dutch heritage villa, balancing historical authenticity with contemporary living requirements. Specialized techniques preserved original architectural details while introducing subtle modern color interpretations.',
      image: 'https://readdy.ai/api/search-image?query=Cape%20Dutch%20heritage%20villa%20in%20Bishopscourt%20with%20traditional%20architecture%2C%20professional%20restoration%20painting%2C%20historic%20property%20with%20gables%2C%20luxury%20estate%20painting&width=400&height=300&seq=bishopscourt-project-4&orientation=landscape'
    }
  ];

  const luxuryChallenges = [
    {
      icon: 'ri-building-4-line',
      title: 'Architectural Complexity',
      description: 'Bishopscourt estates often feature intricate architectural details, high ceilings, and complex layouts requiring specialized equipment and expertise. Our team is equipped with professional scaffolding systems, extension equipment, and the technical skills to safely and effectively paint even the most challenging architectural features.'
    },
    {
      icon: 'ri-time-line',
      title: 'Minimal Disruption',
      description: 'Luxury homeowners require painting services that minimize disruption to daily life and household operations. We offer flexible scheduling including evening and weekend work, staged project phases, and room-by-room completion allowing you to maintain normal residence during the painting process.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Security & Privacy',
      description: 'Working in Bishopscourt\'s exclusive estates requires understanding of security protocols and privacy requirements. All team members are vetted, trained in confidentiality, and experienced in working within secure estate environments while respecting household privacy and security procedures.'
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Premium Finish Standards',
      description: 'Luxury properties demand flawless finishes with no imperfections. Our quality control processes include multiple inspection points, specialized lighting checks to identify any surface irregularities, and refinement work to ensure every surface meets the highest aesthetic standards expected in Bishopscourt estates.'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20 min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20luxury%20estate%20in%20Bishopscourt%20Cape%20Town%2C%20prestigious%20residential%20property%20with%20mountain%20views%2C%20high-end%20painting%20services%2C%20elegant%20architecture&width=1920&height=600&seq=bishopscourt-hero-main&orientation=landscape"
            alt="Premium painting services in Bishopscourt, Cape Town"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Premium <span className="text-yellow-400">Bishopscourt Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Luxury painting services for Bishopscourt\'s most prestigious estates. Expert craftsmanship and premium materials for discerning property owners who demand excellence in every detail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">Painting Bishopscourt\'s Most Exclusive Estates</h2>
            <div className="text-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Bishopscourt stands as one of Cape Town\'s most prestigious and exclusive residential neighborhoods, home to some of the city\'s finest estates and most significant architectural properties. Nestled against the eastern slopes of Table Mountain, this affluent suburb is characterized by sprawling properties, mature gardens, and a remarkable collection of historic and contemporary luxury homes that represent the pinnacle of Cape Town residential architecture.
              </p>
              <p>
                The neighborhood\'s unique character presents distinctive painting requirements that demand specialized expertise and premium materials. From grand Cape Dutch manors and Victorian estates to contemporary architectural masterpieces, Bishopscourt properties require painting services that understand and respect the area\'s architectural heritage while meeting the exacting standards of luxury homeowners. The combination of historic preservation requirements, architectural complexity, and premium finish expectations makes painting in Bishopscourt a specialized discipline.
              </p>
              <p>
                Our painting services in Bishopscourt are specifically designed to meet these unique challenges. We bring extensive experience working with the area\'s diverse architectural styles, from sensitively restoring period properties to applying contemporary finishes on modern estates. Our team understands the importance of discretion, quality, and attention to detail that Bishopscourt homeowners expect, and we\'re equipped with the premium materials, specialized techniques, and professional expertise required to deliver exceptional results on even the most demanding luxury estate projects.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Luxury Painting Services in Bishopscourt</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Bishopscourt\'s exclusive estates and historic properties, delivered with the expertise and discretion your property deserves.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#ffff00' }}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Luxury Challenges Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Understanding Bishopscourt\'s Unique Requirements</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise for the distinctive challenges of luxury estate painting
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {luxuryChallenges.map((challenge, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8">
                <div className="w-14 h-14 flex items-center justify-center mb-6" style={{ backgroundColor: '#ffff00' }}>
                  <i className={`${challenge.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{challenge.title}</h3>
                <p className="text-gray-600 leading-relaxed">{challenge.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Why Bishopscourt Estates Choose Us</h2>
            <p className="text-xl text-gray-600">Excellence in luxury property painting and heritage preservation</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#ffff00' }}>
                  <i className="ri-check-line text-black text-xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-black mb-2">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Our Luxury Painting Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A refined approach designed for Bishopscourt\'s prestigious estates
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{ backgroundColor: '#ffff00' }}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-black mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">Recent Bishopscourt Luxury Projects</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our premium painting projects in Bishopscourt\'s most exclusive estates
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-64">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-black mb-4">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What Bishopscourt Clients Say</h2>
            <p className="text-xl text-gray-600">Testimonials from satisfied luxury estate owners</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-lg" style={{ color: '#ffff00' }}></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">&quot;{testimonial.text}&quot;</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.property}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24" style={{ backgroundColor: '#ffff00' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">Ready to Enhance Your Bishopscourt Estate?</h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for a premium consultation and detailed quote. Experience the difference of luxury painting services designed for Bishopscourt\'s exclusive properties.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
            <a
              href="tel:+27123456789"
              className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
